/** Attribute on the custom-code root wrapper — pairs with scoped CSS. */
export const CUSTOM_CODE_ROOT_ATTR = "data-pb-custom-code-root";

/** Attribute on the page-builder preview root — pairs with scoped page advanced CSS. */
export const PAGE_BUILDER_PREVIEW_ROOT_ATTR = "data-pb-page-preview-root";

/** `builder` scopes CSS with `@scope` so tenant rules do not leak into the dashboard shell. */
export type CustomCodeCssAudience = "builder" | "publish";

export function customCodeScopeSelector(componentId: string): string {
	return `[${CUSTOM_CODE_ROOT_ATTR}="${cssAttributeEscape(componentId)}"]`;
}

export function pageBuilderPreviewScopeSelector(): string {
	return `[${PAGE_BUILDER_PREVIEW_ROOT_ATTR}]`;
}

function cssAttributeEscape(value: string): string {
	if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
		return CSS.escape(value);
	}
	return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

function wrapCssInScope(scopeSelector: string, css: string): string {
	const trimmed = css.trim();
	if (!trimmed) {
		return trimmed;
	}
	const needle = `@scope (${scopeSelector})`;
	if (trimmed.includes(needle)) {
		return trimmed;
	}
	return `@scope (${scopeSelector}) {\n${trimmed}\n}`;
}

function isScopedToComponent(css: string, componentId: string): boolean {
	const needle = `@scope (${customCodeScopeSelector(componentId)})`;
	return css.includes(needle);
}

/**
 * Limit custom-code CSS to descendants of the block root (and :scope on the root).
 * Uses `@scope` so global selectors like `body` / `*` do not leak into the dashboard shell.
 */
export function scopeCustomCodeCss(css: string, componentId: string): string {
	if (!componentId.trim()) {
		return css.trim();
	}
	if (isScopedToComponent(css, componentId)) {
		return css.trim();
	}
	return wrapCssInScope(customCodeScopeSelector(componentId), css);
}

/** Strip a single outer `@scope (…) { … }` wrapper (persisted snapshots / publish render). */
export function unscopeCustomCodeCss(css: string): string {
	const trimmed = css.trim();
	if (!trimmed) {
		return trimmed;
	}
	const match = /^@scope\s*\([^)]*\)\s*\{([\s\S]*)\}\s*$/.exec(trimmed);
	if (!match) {
		return trimmed;
	}
	return match[1]?.trim() ?? trimmed;
}

export function resolveCustomCodeCssForAudience(
	css: string,
	componentId: string,
	audience: CustomCodeCssAudience
): string {
	const trimmed = css.trim();
	if (!trimmed) {
		return trimmed;
	}
	if (audience === "publish") {
		return unscopeCustomCodeCss(trimmed);
	}
	return scopeCustomCodeCss(trimmed, componentId);
}

/**
 * Limit page advanced CSS to the builder canvas preview root.
 * Builder-only — region instance keeps unscoped head/body/footer tags.
 */
export function scopePageBuilderPreviewCss(css: string): string {
	return wrapCssInScope(pageBuilderPreviewScopeSelector(), css);
}

/** Rewrite a leading `<style>` block in staticHtml for builder preview or publish. */
export function prepareLeadingStyleInStaticHtml(
	staticHtml: string,
	componentId: string,
	audience: CustomCodeCssAudience = "builder"
): string {
	const trimmed = staticHtml.trim();
	if (!trimmed) {
		return trimmed;
	}

	const stylePrefixMatch = trimmed.match(/^<style\b[^>]*>([\s\S]*?)<\/style>/i);
	if (!stylePrefixMatch) {
		return trimmed;
	}

	const rawCss = stylePrefixMatch[1] ?? "";
	const preparedCss = resolveCustomCodeCssForAudience(
		rawCss,
		componentId,
		audience
	);
	const remainder = trimmed.slice(stylePrefixMatch[0].length);
	return `<style>${preparedCss}</style>${remainder}`;
}

/** @deprecated Use `prepareLeadingStyleInStaticHtml` */
export function scopeLeadingStyleInStaticHtml(
	staticHtml: string,
	componentId: string
): string {
	return prepareLeadingStyleInStaticHtml(staticHtml, componentId, "builder");
}
